<template>
    <div id="page-website-settings">
        <div class="page-title-box" ref="pageTitle" :class="[stickyClass]">
            <div class="page-title-right form-actions">
                <div
                    class="alert bg-white text-danger"
                    role="alert"
                    v-if="formShowErrors"
                >Please check your input and try again</div>
                <button
                    @click="revert()"
                    class="btn btn-light btn-revert"
                    :disabled="loading || saving"
                >Revert</button>
                <button
                    @click="save()"
                    class="btn btn-primary btn-save"
                    :disabled="loading || saving"
                >
                    <span
                        class="spinner-border spinner-border-sm mr-1"
                        role="status"
                        aria-hidden="true"
                        v-if="saving"
                    ></span>Save
                </button>
            </div>

            <div class="page-title-left">
                <h2 class="page-title">Website Settings</h2>
                <p v-if="website.expiry_label">Subscription valid until: {{ website.expiry_label }}</p>
            </div>
        </div>
        <div class="alert alert-success" role="alert" v-if="success">Website settings saved.</div>
        <div class="row">
            <div class="col-md-6">
                <div class="card">
                    <div class="card-body">
                        <h4 class="header-title">General Settings</h4>
                        <p class="text-muted font-14 mb-3">Enter the details of your website below</p>
                        <div class="form-group row mb-3">
                            <label
                                for="website-name"
                                class="col-sm-3 col-md-3 col-form-label"
                            >Website Name</label>
                            <div class="col-sm-8 col-md-9">
                                <input
                                    type="text"
                                    id="website-name"
                                    class="form-control"
                                    v-model="form.name"
                                    :class="[hasErrorInputClass('name')]"
                                />
                                <span class="help-block">
                                    <small>Enter the name of your website</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['name']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <div class="col-sm-3 col-md-3 col-form-label">
                                <label>Domains</label>
                                <form-error-label
                                    :target="formErrors['domains']"
                                    error-class="invalid-feedback"
                                    style="display: block"
                                    role="alert"
                                ></form-error-label>
                            </div>
                            <div class="col-sm-8 col-md-9">
                                <ul class="domain-list">
                                    <li v-for="(domain, index) in form.domains" :key="index">
                                        <div class="domain-container">
                                            <input
                                                type="text"
                                                class="form-control"
                                                placeholder="http://www.yourdomain.com"
                                                v-model="form.domains[index]"
                                                :class="[hasErrorInputClass('domains.' + index)]"
                                            />
                                            <form-error-label
                                                :target="formErrors['domains.' + index]"
                                                error-class="invalid-feedback"
                                                role="alert"
                                            ></form-error-label>
                                        </div>
                                        <button
                                            class="btn btn-outline-danger btn-rounded btn-remove"
                                            aria-label="Remove"
                                            @click="removeDomain(index)"
                                            :disabled="form.domains.length <= 1"
                                        >
                                            <i class="dripicons-minus" aria-hidden="true"></i>
                                        </button>
                                        <button
                                            class="btn btn-outline-secondary btn-rounded btn-add"
                                            aria-label="Add"
                                            @click="addDomain()"
                                            v-if="index == form.domains.length-1 && form.domains.length < 10"
                                        >
                                            <i class="dripicons-plus" aria-hidden="true"></i>
                                        </button>
                                    </li>
                                </ul>
                                <span class="help-block">
                                    <small>All variations including http:// and https://, with and/or without www</small>
                                </span>
                            </div>
                        </div>
                        <div class="form-group row mb-0">
                            <label class="col-sm-3 col-md-3 col-form-label">Desktop mode</label>
                            <div class="col-sm-12 col-md-9 col-form-label">
                                <custom-switch
                                    v-model="form.desktop_mode"
                                    :class="[hasErrorInputClass('desktop_mode')]"
                                ></custom-switch>
                                <span class="help-block">
                                    <small>Enable 3D viewer on personal computers</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['desktop_mode']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="col-md-6">
                <div class="card">
                    <div class="card-body">
                        <div class="form-group row mb-3">
                            <label class="col-sm-4 col-md-3 col-form-label">State</label>
                            <div class="col-sm-8 col-md-9 col-form-label">
                                <div class="custom-control custom-switch" v-if="false">
                                    <input
                                        type="checkbox"
                                        v-model="form.state"
                                        class="custom-control-input"
                                        id="option-state"
                                        :class="[hasErrorInputClass('state')]"
                                    />
                                    <label class="custom-control-label" for="option-state">Active</label>
                                </div>
                                <custom-switch
                                    v-model="form.state"
                                    :class="[hasErrorInputClass('state')]"
                                ></custom-switch>
                                <span class="help-block">
                                    <small>Turn On / Off Seek Peek on your website</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['state']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label class="col-sm-4 col-md-3 col-form-label">App Key</label>
                            <div class="col-sm-8 col-md-9">
                                <div class="input-group">
                                    <input
                                        @click="selectAll($event)"
                                        type="text"
                                        class="form-control"
                                        readonly="readonly"
                                        :value="website.app_key"
                                    />
                                    <button class="btn btn-primary" @click="copyAppKey()">Copy</button>
                                </div>
                                <span class="help-block">
                                    <small>
                                        This App Key is to be used for
                                        <a
                                            href="https://seek-peek.com/installation-wordpress"
                                            target="_blank"
                                        >WordPress</a> or
                                        <a
                                            href="https://seek-peek.com/installation-shopify"
                                            target="_blank"
                                        >Shopify</a> Plugin.
                                    </small>
                                </span>
                                <div
                                    class="alert alert-primary mt-1"
                                    role="alert"
                                    v-if="app_key_copied"
                                >App Key copied!</div>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label class="col-sm-4 col-md-3 col-form-label">API Version</label>
                            <div class="col-sm-8 col-md-9">
                                <select
                                    id="website-api-version"
                                    class="form-control"
                                    v-model="form.api_version"
                                    :class="[hasErrorInputClass('api_version')]"
                                >
                                    <option value="0">Latest version</option>
                                    <option
                                        v-for="api in api_versions"
                                        :key="api.id"
                                        v-text="api.name"
                                        :value="api.id"
                                    ></option>
                                </select>
                                <span class="help-block">
                                    <small>Select API version (Select "Latest" if unsure)</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['api_version']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-0">
                            <label class="col-sm-4 col-md-3 col-form-label">HTML Code</label>
                            <div class="col-sm-8 col-md-9">
                                <textarea
                                    class="html-code-panel mb-0"
                                    @click="selectAll($event.target)"
                                    readonly
                                    v-text="compiledHTMLSnippet"
                                    ref="htmlSnippet"
                                ></textarea>
                                <span class="help-block mb-2" style="display:block">
                                    <small>Copy this code to the "head" of your site if installing manually</small>
                                </span>
                                <button
                                    class="btn btn-primary"
                                    @click="copyHTMLCode()"
                                >Copy HTML Code</button>

                                <div
                                    class="alert alert-primary mt-1"
                                    role="alert"
                                    v-if="html_code_copied"
                                >HTML Code copied!</div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <h4 class="header-title">Button Settings</h4>
                <p class="text-muted font-14 mb-3">Set how you want the button to look</p>
                <div class="row button-settings-container">
                    <div class="col-md-6 button-form-group">
                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Width</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.icon_width"
                                    :class="[hasErrorInputClass('icon_width')]"
                                />
                                <span class="help-block">
                                    <small>The width of your button in px or % (i.e. 180px or 100%)</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['icon_width']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Height</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.icon_height"
                                    :class="[hasErrorInputClass('icon_height')]"
                                />
                                <span class="help-block">
                                    <small>The height of your button in px or % (i.e. 180px or 100%)</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['icon_height']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label
                                for="button-color"
                                class="col-sm-2 col-md-3 col-form-label"
                            >Colour</label>
                            <div class="col-sm-8 col-md-9">
                                <div class="color-picker-component">
                                    <select
                                        id="button-color"
                                        class="form-control"
                                        v-model="colorPickerValue"
                                        :class="[hasErrorInputClass('icon_colour')]"
                                    >
                                        <option
                                            v-for="color in normalizedButtonColors"
                                            :key="color.hex"
                                            :value="color.hex"
                                            v-text="color.name"
                                        ></option>
                                        <option :key="-1" :value="-1">Custom</option>
                                    </select>
                                    <input
                                        class="custom-button-color"
                                        type="color"
                                        v-model="form.icon_colour"
                                        v-if="custom_color"
                                    />
                                </div>
                                <span class="help-block">
                                    <small>Base colour of the button</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['icon_colour']"
                                    error-class="invalid-feedback d-block"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Border Radius</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.border_radius"
                                    :class="[hasErrorInputClass('border_radius')]"
                                />
                                <span class="help-block">
                                    <small>Border radius of button in pixels</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['border_radius']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label
                                for="text-color"
                                class="col-sm-2 col-md-3 col-form-label"
                            >Text Colour</label>
                            <div class="col-sm-8 col-md-9">
                                <select
                                    id="text-color"
                                    class="form-control"
                                    v-model="form.logo_colour"
                                    :class="[hasErrorInputClass('logo_colour')]"
                                >
                                    <option
                                        v-for="color in text_colors"
                                        :key="color.id"
                                        :value="color.id"
                                        v-text="color.name"
                                    ></option>
                                </select>
                                <span class="help-block">
                                    <small>Colour of text on button</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['logo_colour']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>

                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Margin Left</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.margin_left"
                                    :class="[hasErrorInputClass('margin_left')]"
                                />
                                <span class="help-block">
                                    <small>Space on the left in px</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['margin_left']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Margin Right</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.margin_right"
                                    :class="[hasErrorInputClass('margin_right')]"
                                />
                                <span class="help-block">
                                    <small>Space on the right in px</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['margin_right']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Margin Top</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.margin_top"
                                    :class="[hasErrorInputClass('margin_top')]"
                                />
                                <span class="help-block">
                                    <small>Space on the left in px</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['margin_top']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                        <div class="form-group row mb-3">
                            <label for class="col-sm-2 col-md-3 col-form-label">Margin Bottom</label>
                            <div class="col-sm-6 col-md-9">
                                <input
                                    type="text"
                                    id="button-width"
                                    class="form-control"
                                    v-model="form.margin_bottom"
                                    :class="[hasErrorInputClass('margin_bottom')]"
                                />
                                <span class="help-block">
                                    <small>Space on the left in px</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['margin_bottom']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-6 button-preview">
                        <span class="label">Preview</span>
                        <div :style="buttonStyle">
                            <div
                                style="width:80%; height:70%; margin: 0; position: relative; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align:center;"
                            >
                                <img
                                    style="width:100%; height:100%; object-fit:contain;"
                                    :src="buttonARUrl"
                                />
                            </div>
                        </div>
                        <div :style="buttonStyle">
                            <div
                                style="width:80%; height:70%; margin: 0; position: relative; top: 50%; left: 50%; transform: translate(-50%, -50%); text-align:center;"
                            >
                                <img
                                    style="width:100%; height:100%; object-fit:contain;"
                                    :src="button3DUrl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="card">
            <div class="card-body">
                <h4 class="header-title">Placement Settings</h4>
                <p class="text-muted font-14 mb-3">Set where you want the button to appear</p>

                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group row mb-3">
                            <label class="col-sm-3 col-md-3 col-form-label">Insert ID</label>
                            <div class="col-sm-8 col-md-9">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="form.insert_id"
                                    :class="[hasErrorInputClass('insert_id')]"
                                />
                                <span class="help-block">
                                    <small>Insert the reference element's ID</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['insert_id']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>

                    <div class="col-md-1 mb-3">
                        <label class="col-form-label or-label">- OR -</label>
                    </div>
                    <div class="col-md-5">
                        <div class="form-group row mb-3">
                            <label class="col-sm-3 col-md-3 col-form-label">Insert Class</label>
                            <div class="col-sm-8 col-md-9">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="form.insert_class"
                                    :class="[hasErrorInputClass('insert_class')]"
                                />
                                <span class="help-block">
                                    <small>Insert the reference element's Class</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['insert_class']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group row mb-3">
                            <label class="col-sm-3 col-md-3 col-form-label">Insert Position</label>
                            <div class="col-sm-8 col-md-6">
                                <select
                                    class="form-control"
                                    v-model="form.insert_position"
                                    :class="[hasErrorInputClass('insert_position')]"
                                >
                                    <option value="beforebegin">beforebegin</option>
                                    <option value="afterbegin">afterbegin</option>
                                    <option value="beforeend">beforeend</option>
                                    <option value="afterend">afterend</option>
                                </select>
                                <span class="help-block">
                                    <small>Where to put the button in reference to the reference element</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['insert_position']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                </div>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group row mb-3">
                            <label class="col-sm-3 col-md-3 col-form-label">Force Insert</label>
                            <div class="col-9 col-form-label">
                                <div class="custom-control custom-switch" v-if="false">
                                    <input
                                        type="checkbox"
                                        v-model="form.force_insert"
                                        class="custom-control-input"
                                        id="option-force-insert"
                                        :class="[hasErrorInputClass('force_insert')]"
                                    />
                                    <label
                                        class="custom-control-label"
                                        for="option-force-insert"
                                    >Active</label>
                                </div>

                                <custom-switch
                                    v-model="form.force_insert"
                                    :class="[hasErrorInputClass('force_insert')]"
                                ></custom-switch>
                                <span class="help-block">
                                    <small>Insert button after H1 tag if reference element is not found</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['force_insert']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <div class="card">
            <div class="card-body">
                <h4 class="header-title">Conversion Tracking (Beta)</h4>
                <p class="text-muted font-14 mb-3">Track "Add to Cart" activities on your products</p>
                <div class="row">
                    <div class="col-md-6">
                        <div class="form-group row">
                            <label class="col-sm-3 col-md-3 col-form-label">Cart ID</label>
                            <div class="col-sm-8 col-md-9">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="form.cart_id"
                                    :class="[hasErrorInputClass('cart_id')]"
                                />
                                <span class="help-block">
                                    <small>Insert the ID of your "Add to Cart" button</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['cart_id']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                    <div class="col-md-1">
                        <label class="col-form-label or-label">- OR -</label>
                    </div>
                    <div class="col-md-5">
                        <div class="form-group row">
                            <label class="col-sm-3 col-md-3 col-form-label">Cart Class</label>
                            <div class="col-sm-8 col-md-9">
                                <input
                                    type="text"
                                    class="form-control"
                                    v-model="form.cart_class"
                                    :class="[hasErrorInputClass('cart_class')]"
                                />
                                <span class="help-block">
                                    <small>Insert the Class of your "Add to Cart" button</small>
                                </span>
                                <form-error-label
                                    :target="formErrors['cart_class']"
                                    error-class="invalid-feedback"
                                    role="alert"
                                ></form-error-label>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <loading v-model="loading">Loading...</loading>
    </div>
</template>

<script>
import { cloneDeep, findIndex, map } from "lodash";
import { defineComponent } from "@vue/composition-api";
import { EventBus } from "@/utils/EventBus";

import website from "@/client/api/website";

import FormErrorLabel from "@/ui/FormErrorLabel";
import useFormErrorHandler from "@/utils/FormErrorHandler";
import useStickyHandler from "@/ui/UseStickyHandler";

import CustomSwitch from "@/ui/CustomSwitch";
import Loading from "@/ui/Loading";
import { mapState } from "vuex";

export default defineComponent({
    components: {
        FormErrorLabel,
        Loading,
        CustomSwitch,
    },
    data() {
        return {
            loading: false,
            saving: false,
            website: {},
            form: {},
            // icons: [],
            // icon_colors: [],
            // icon_shapes: [],
            text_colors: [],
            button_colors: [],
            api_versions: [],
            success: false,
            app_key_copied: false,
            html_code_copied: false,
            custom_color: false,
        };
    },
    watch: {
        $route: {
            handler: "loadData",
            immediate: true,
        },
    },
    setup() {
        let {
            formErrors,
            formShowErrors,
            formErrorMessage,
            resetFormError,
            handleFormError,
            hasError,
            hasErrorInputClass,
        } = useFormErrorHandler();

        let { el: pageTitle, sticky, stickyClass } = useStickyHandler();

        return {
            // Form Error Handler
            formErrors,
            formErrorMessage,
            formShowErrors,
            resetFormError,
            handleFormError,
            hasError,
            hasErrorInputClass,
            // Sticky Handler
            sticky,
            stickyClass,
            pageTitle,
        };
    },
    methods: {
        selectAll(target) {
            if (this.isMobile) {
                target.setSelectionRange(0, target.value.length);
            } else {
                target.select();
            }
        },
        async loadData() {
            this.loading = true;

            try {
                let res = await website.getWebsite(this.websiteId);

                if (res.data && res.data.data) {
                    this.website = res.data.data;
                    // this.form = cloneDeep(this.website);
                    this.revert();
                }

                if (res.data && res.data.meta && res.data.meta.text_colors) {
                    this.text_colors = res.data.meta.text_colors;
                }
                if (res.data && res.data.meta && res.data.meta.button_colors) {
                    this.button_colors = res.data.meta.button_colors;
                }

                if (res.data && res.data.meta && res.data.meta.api_versions) {
                    this.api_versions = res.data.meta.api_versions;
                }

                // if (res.data && res.data.meta && res.data.meta.icon_colors) {
                //     this.icon_colors = res.data.meta.icon_colors;
                // }

                // if (res.data && res.data.meta && res.data.meta.icon_shapes) {
                //     this.icon_shapes = res.data.meta.icon_shapes;
                // }

                // if (res.data && res.data.meta && res.data.meta.icons) {
                //     this.icons = res.data.meta.icons;
                // }
            } catch (e) {
            } finally {
                this.loading = false;
            }
        },
        revert() {
            this.form = cloneDeep(this.website);
            if (!this.form.domains || this.form.domains.length == 0) {
                this.addDomain();
            }
        },
        addDomain() {
            this.form.domains.push("");
        },
        removeDomain(index) {
            this.form.domains.splice(index, 1);
        },
        async save() {
            this.resetFormError();
            this.success = false;
            this.saving = true;

            try {
                let res = await website.putWebsite(this.websiteId, this.form);

                if (res.data && res.data.status == "ok") {
                    this.success = true;
                    this.loadData();

                    EventBus.$emit("scroll-top");

                    setTimeout(() => {
                        this.success = false;
                    }, 3000);
                }
                // console.log(res);
            } catch (e) {
                this.handleFormError(e);
            } finally {
                this.saving = false;
            }
        },
        async copyContent(field_content) {
            // this.selectAll(this.$refs[]);

            return new Promise((resolve, reject) => {
                let onCopy = (e) => {
                    e.clipboardData.setData("text/plain", field_content);
                    e.preventDefault();
                    resolve();
                };

                this.$nextTick(() => {
                    document.addEventListener("copy", onCopy);
                    document.execCommand("copy");
                    document.removeEventListener("copy", onCopy);
                });
            });
        },
        async copyAppKey() {
            await this.copyContent(this.website.app_key);
            this.app_key_copied = true;
            setTimeout(() => {
                this.app_key_copied = false;
            }, 3000);
        },
        async copyHTMLCode() {
            await this.copyContent(this.compiledHTMLSnippet);
            this.html_code_copied = true;
            setTimeout(() => {
                this.html_code_copied = false;
            }, 3000);
        },
        findColor(val) {
            const index = findIndex(this.normalizedButtonColors, {
                hex: val,
            });
            return index;
        },
    },
    computed: {
        websiteId() {
            return this.$route.params.site_id;
        },
        currentIcon() {
            if (this.form.logo_colour) {
                let index = findIndex(this.text_colors, {
                    id: this.form.logo_colour,
                });
                if (index != -1) {
                    return this.text_colors[index];
                }
            }
        },
        buttonARUrl() {
            if (this.currentIcon) {
                return this.currentIcon.ar_icon_path;
            }
        },
        button3DUrl() {
            if (this.currentIcon) {
                return this.currentIcon["3d_icon_path"];
            }
        },
        buttonStyle() {
            let width = this.form.icon_width
                ? this.form.icon_width
                : 180 + "px";
            let height = this.form.icon_height
                ? this.form.icon_height
                : 50 + "px";
            let backgroundColor = this.form.icon_colour
                ? this.form.icon_colour
                : "#0074AA";
            let borderRadius =
                (this.form.border_radius ? this.form.border_radius : 0) + "px";

            return { width, height, backgroundColor, borderRadius };
        },
        compiledHTMLSnippet() {
            if (
                this.website
                    .html_snippet /* &&
                this.website.html_app_version_snippet */
            ) {
                let app_version = this.form.app_version;
                let app_version_snippet = app_version;
                // ? this.website.html_app_version_snippet.replace(
                //       "###APP_VERSION###",
                //       app_version
                //   )
                // : "";

                let app_key = this.website.app_key ? this.website.app_key : "";

                return this.website.html_snippet.replace(
                    "###APP_KEY###",
                    app_key
                );
                // .replace("###APP_VERSION_SNIPPET###", app_version_snippet);
            }
        },
        ...mapState(["currentWebsiteId"]),
        colorPickerValue: {
            get() {
                const index = this.findColor(this.form.icon_colour);
                if (this.custom_color && index == -1) {
                    return -1;
                }
                return this.form.icon_colour;
                // if (this.form.icon_colour) {
                //     const index = findIndex(this.button_colors, {
                //         hex: this.form.icon_colour
                //     });
                //     if (index != -1) {
                //         this.custom_color = false;
                //         return this.form.icon_colour;
                //     }
                //     this.custom_color = true;
                //     return this.form.icon_colour;
                // }
            },
            set(val) {
                const last_color = this.form.icon_colour;
                const index = this.findColor(val);
                this.custom_color = val == -1 || index == -1;
                this.form.icon_colour = this.custom_color ? last_color : val;
            },
        },
        normalizedButtonColors() {
            return map(this.button_colors, (val) => {
                return {
                    hex: val.hex.toLowerCase(),
                    name: val.name,
                    id: val.id,
                };
            });
        },
        // showingColorPicker() {
        //     return this.colorPickerValue == -1;
        // }
    },
});
</script>

<style lang="scss">
.or-label {
    width: 100%;
    text-align: center;
}

.html-code-panel {
    display: block;
    width: 100%;
    border: 1px solid #ccc;
    border-radius: rem-calc(5);
    padding: rem-calc(10);
    min-height: rem-calc(100);
}

.domain-list {
    list-style: none;
    margin: 0;
    padding: 0;

    li {
        // height: rem-calc(36);
        margin-bottom: rem-calc(6);
        display: grid;
        grid-template-columns: 1fr repeat(2, #{rem-calc(24)});
        gap: rem-calc(12);
        align-items: center;

        // & > :first-child {
        //     flex: 1;
        // }

        & > input {
            grid-column: 1 / 2;
        }

        & > button {
            flex-shrink: 0;
            // margin-left: rem-calc(12);
            padding: 0;
            width: rem-calc(24);
            height: rem-calc(24);
            display: flex;
            align-items: center;
            justify-content: center;
            align-self: flex-start;
            margin-top: rem-calc((36 - 24 + 3) / 2);

            & > i {
                line-height: 0;
            }

            &.btn-remove {
                grid-column: 2 / 3;
            }
            &.btn-add {
                grid-column: 3 / 4;
            }
        }

        // &.create-form {
        //     height: rem-calc(38);
        //     margin-bottom: rem-calc(4);
        // }
    }
}

.button-settings-container {
    // display: grid;
    // grid-template-columns: 1fr;

    // @include ipad {
    //     grid-template-columns: 2fr 1fr;
    // }

    .button-preview {
        background-color: white;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;

        // & > img {
        //     margin: rem-calc(10);
        // }

        // & > span.label {
        //     margin-top: rem-calc(10);
        //     font-size: rem-calc(14);
        // }

        & > * {
            margin: rem-calc(10);
        }
    }
}

.color-picker-component {
    display: flex;
    // align-items: center;

    .custom-button-color {
        display: block;
        min-width: 30%;
        // margin: 0;
        margin-left: rem-calc(5);
        padding: 0;
        border: 0;
        outline: none;
        height: calc(2.2125rem + 2px);
    }
}
</style>